import { useMemo } from "react"

import { ENTITY_ICONS } from "@/constants/entity-icons"
import { usePersonasList } from "@/persona"

import { FilterPopover } from "@/components/filters/FilterPopover"
import { isFilterSectionDisabled } from "@/components/filters/filterSectionVisibility"
import { FilterTrigger } from "@/components/filters/FilterTrigger"
import { SearchableCheckboxList } from "@/components/filters/SearchableCheckboxList"

import { useRunsFilter } from "../../contexts/RunsFilterContext"
import type { FilterSectionProps } from "../../types/filters"

const PersonaIcon = ENTITY_ICONS.personas

export const PersonaFilterSection: React.FC<FilterSectionProps> = ({
  isOpen,
  onToggle,
  filterEnabled,
}) => {
  const { personaFilters, setPersonaFilters } = useRunsFilter()
  const {
    data: personas,
    isError,
    isLoading,
  } = usePersonasList({
    enabled: filterEnabled,
  })

  const items = useMemo(() => {
    if (!personas) {
      return []
    }
    return personas.map((persona) => ({
      id: persona.id,
      label: persona.display_name || `Persona ${persona.id}`,
      searchValue: persona.display_name || "",
    }))
  }, [personas])

  const isDisabled = isFilterSectionDisabled({
    activeFilterCount: personaFilters.length,
    isDeferred: filterEnabled === false,
    isError,
    isLoading,
    optionCount: items.length,
  })

  return (
    <FilterPopover
      isOpen={isOpen}
      onToggle={onToggle}
      width="w-72"
      disabled={isDisabled}
      emptyMessage="No personas available in this workspace."
      trigger={
        <FilterTrigger
          label="Personas"
          icon={<PersonaIcon className="w-4 h-4" />}
          isActive={personaFilters.length > 0}
          disabled={isDisabled}
        />
      }
    >
      <SearchableCheckboxList
        items={items}
        selectedIds={personaFilters}
        onSelectionChange={setPersonaFilters}
        isLoading={isLoading}
        isDeferred={filterEnabled === false}
        isOpen={isOpen}
        placeholder="Search personas..."
        emptyMessage="No characters found"
      />
    </FilterPopover>
  )
}
